import {
  Angle,
  ElementType,
  Fixture,
  formatLengthValue,
  formatThicknessValue,
  getThickness,
  Group,
  LENGTH_INPUT_REGEX,
  parseLength,
  parseThickness,
  Polyline,
  Rect,
  Symbol as SymbolType,
  THICKNESS_INPUT_REGEX,
  Trapezoid,
  Triangle,
} from '@ritabygglov/core';
import * as KeyCode from 'keycode-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAppState } from '../../context/AppState/useAppState';
import { transformGroup } from '../../helpers/groupHelpers';
import {
  copy,
  getDistance,
  resizePolylineSegment,
  resolvePolylinePoint,
} from '../../helpers/utility';
import { useElementUpdate } from '../../hooks/useElementUpdate';
import { useMeasurement } from '../../hooks/useMeasurement';
import { createUpdateChangeAction } from '../../services/ChangeTypes';
import { SelectionModes } from '../../types';
import { computeTrapezoidVertices } from '../../services/Actions/Trapezoid';

const Input = styled.input<{ $wide?: boolean }>`
  border: none;
  max-width: ${(props) => (props.$wide ? '64px' : '40px')};
  height: 24px;
  margin-right: 4px;
  margin-left: 4px;
  padding-left: 4px;
  border-radius: 4px;
  display: inline-block;
  background-color: #fff;
  border: 1px solid var(--border-light);
  &:hover {
    border: solid 1px var(--border);
  }
`;

export const UnitIndicator = styled.span`
  padding: 0;
  padding-right: 8px;
  color: rgba(0, 0, 0, 0.48);
  font-size: 0.85em;
  pointer-events: none;
`;

export const InputField = ({
  element,
  property,
  segmentIndex = 0,
  format,
  converter,
  regex,
}: {
  element:
    | Rect
    | Fixture
    | SymbolType
    | Triangle
    | Trapezoid
    | Angle
    | Polyline
    | Group;
  property:
    | 'width'
    | 'height'
    | 'rotate'
    | 'pointRatio'
    | 'topWidth'
    | 'bottomWidth'
    | 'length'
    | 'thickness';
  segmentIndex?: number;
  format?: (value: string | number) => string;
  converter?: (value: string) => number;
  regex?: RegExp;
}) => {
  const { session, selectionMode, setSelectionMode, commandManager } =
    useAppState();
  const { updateGroup } = useElementUpdate();
  const userId = session.currentUserId;
  const { unitSystem, lengthFormat, scalingFactor } = useMeasurement();
  const isThickness = property === 'thickness';

  // Defaults for a plain length/thickness field, used whenever the caller
  // doesn't pass an explicit format/converter/regex (e.g. for rotate or
  // pointRatio, which always do).
  const defaultFormat = useCallback(
    (value: string | number) =>
      isThickness
        ? formatThicknessValue(Number(value), unitSystem, lengthFormat)
        : formatLengthValue(Number(value), unitSystem, lengthFormat),
    [isThickness, unitSystem, lengthFormat]
  );
  const defaultConverter = useCallback(
    (value: string) =>
      isThickness
        ? parseThickness(value, unitSystem, lengthFormat)
        : parseLength(value, unitSystem, lengthFormat),
    [isThickness, unitSystem, lengthFormat]
  );
  const defaultRegex = isThickness
    ? THICKNESS_INPUT_REGEX[unitSystem]
    : LENGTH_INPUT_REGEX[unitSystem];

  const effectiveFormat = format ?? defaultFormat;
  const effectiveConverter = converter ?? defaultConverter;
  const effectiveRegex = regex ?? defaultRegex;

  const getPropertyValue = () => {
    if (property === 'thickness') {
      return getThickness(element);
    }
    if (property === 'length' && element.type === ElementType.Polyline) {
      const p0 = resolvePolylinePoint(element, segmentIndex);
      const p1 = resolvePolylinePoint(element, segmentIndex + 1);
      if (!p0 || !p1) return 0;
      return getDistance({ x: p0.x, y: p0.y }, { x: p1.x, y: p1.y });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (element as any)[property] as number;
  };
  const [value, setValue] = useState(effectiveFormat(getPropertyValue()));
  const ref = useRef(null);
  const elementState = getPropertyValue();

  const isUnscaled = property === 'rotate' || property === 'pointRatio';
  const displayValue = isUnscaled ? elementState : elementState * scalingFactor;
  const currentValue = effectiveFormat(
    property === 'pointRatio' ? displayValue * 100 : displayValue
  );

  useEffect(() => {
    if (
      currentValue !== value &&
      selectionMode.type !== SelectionModes.Updating
    ) {
      setValue(currentValue);
    }
  }, [currentValue, selectionMode.type, value]);

  const saveChanges = () => {
    if (!element || value === currentValue) return;

    if (property === 'length' && element.type === ElementType.Polyline) {
      const newLengthCanvas = effectiveConverter(value) / scalingFactor;
      const newElement = resizePolylineSegment(
        element,
        segmentIndex,
        newLengthCanvas
      );
      if (!newElement) return;
      createUpdateChangeAction(
        newElement,
        element,
        false,
        commandManager,
        userId
      );
      return;
    }

    let convertedValue = isUnscaled
      ? effectiveConverter(value)
      : effectiveConverter(value) / scalingFactor;

    if (property === 'pointRatio') {
      convertedValue = Math.max(0, Math.min(1, convertedValue / 100));
    }

    if (property === 'thickness') {
      convertedValue = Math.max(0, convertedValue);
    }

    if (element.type === ElementType.Group) {
      updateGroup(element, (group, children) =>
        transformGroup(group, children, { [property]: convertedValue })
      );
      return;
    }

    const el = copy(element);
    let newElement = { ...el, [property]: convertedValue };

    if (el.type === ElementType.Trapezoid) {
      const trap = newElement as Trapezoid;
      const computed = computeTrapezoidVertices(
        trap.topWidth,
        trap.bottomWidth,
        trap.height
      );
      if (!computed) return;
      newElement = { ...trap, width: computed.width };
    }

    createUpdateChangeAction(newElement, el, false, commandManager, userId);
  };

  return (
    <Input
      type="text"
      id="property-option"
      $wide={unitSystem === 'imperial' || lengthFormat === 'millimeters'}
      value={value}
      ref={ref}
      onFocus={() => {
        setSelectionMode({
          elementType: undefined,
          type: SelectionModes.Updating,
        });
      }}
      onChange={(e) => {
        const isValid = effectiveRegex.test(e.target.value);
        if (isValid) {
          setValue(e.target.value);
        }
      }}
      onKeyDown={(e) => {
        if (e.code === KeyCode.CODE_ENTER) {
          saveChanges();
        }
      }}
      onBlur={() => {
        saveChanges();
      }}
    />
  );
};
